import type { SortItem, SortEvent } from './types';

/** Apply events up to `eventIndex` on a deep-cloned items array, returning the final state. */
export function computeState(items: SortItem[], events: SortEvent[], eventIndex: number): SortItem[] {
  const arr: SortItem[] = items.map(item => ({ ...item, currentIndex: item.currentIndex }));

  for (let i = 0; i < Math.min(eventIndex, events.length); i++) {
    const e = events[i];
    if (!e) continue;

    switch (e.type) {
      case 'swap': {
        const [a, b] = e.indices;
        const t = arr[a]; arr[a] = arr[b]; arr[b] = t;
        if (arr[a]) arr[a].currentIndex = a;
        if (arr[b]) arr[b].currentIndex = b;
        break;
      }
      case 'overwrite':
        arr[e.index] = { ...e.value, currentIndex: e.index };
        arr[e.index].currentIndex = e.index;
        break;
    }
  }

  // Now compute statuses by scanning events in forward order
  const statuses = new Map<number, string>();
  for (let i = 0; i < Math.min(eventIndex, events.length); i++) {
    const e = events[i];
    if (!e) continue;

    if (e.type === 'mark') {
      statuses.set(e.index, e.status);
    } else if (e.type === 'compare') {
      statuses.set(e.indices[0], 'comparing');
      statuses.set(e.indices[1], 'comparing');
    } else if (e.type === 'swap') {
      statuses.set(e.indices[0], 'swapping');
      statuses.set(e.indices[1], 'swapping');
    } else if (e.type === 'pivot') {
      statuses.set(e.index, 'pivot');
    } else if (e.type === 'done') {
      for (const idx of e.indices) statuses.set(idx, 'sorted');
    } else if (e.type === 'overwrite') {
      statuses.set(e.index, 'selected');
    }
  }

  for (const [idx, st] of statuses) {
    if (arr[idx]) (arr[idx] as any)._status = st;
  }

  return arr;
}
